import { useEffect, useState } from "react";
import { useLead } from "@/context/LeadContext";
import Icon from "@/components/Icon";
import { createLead } from "@/lib/api";

const INTERESTS = [
  "Unified EHR / EMR",
  "Practice & RCM",
  "Enterprise Integration",
  "Patient / Provider / Employer Portals",
  "Telehealth & Remote Care",
  "Analytics & Automation",
  "General Inquiry",
];

const EMPTY = { name: "", email: "", organization: "", phone: "", interest: "", message: "" };

export default function LeadModal() {
  const { open, mode, closeLead } = useLead();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const isDemo = mode === "demo";

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setStatus("idle");
      setError("");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeLead();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeLead]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Please provide your name and email.");
      return;
    }
    setStatus("loading");
    try {
      await createLead({ ...form, type: mode });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" data-testid="lead-modal" onMouseDown={closeLead}>
      <div className="modal__panel" onMouseDown={(e) => e.stopPropagation()}>
        <button className="modal__close" onClick={closeLead} aria-label="Close" data-testid="lead-modal-close">
          <Icon name="X" size={18} />
        </button>

        <div className="modal__head">
          <img src="/assets/logo-ums-header.png" alt="Proactive UMS+" className="modal__logo" />
          <h3 className="modal__title">{isDemo ? "Request a Demo" : "Talk to Our Team"}</h3>
          <p className="modal__sub">
            {isDemo
              ? "See how the PUMS ecosystem can connect care, technology and operations for your organization."
              : "Tell us about your goals — our team will reach out to build a healthier tomorrow with you."}
          </p>
        </div>

        {status === "success" ? (
          <div className="modal__success" data-testid="lead-success">
            <span className="modal__success-ic"><Icon name="Heart" size={30} /></span>
            <h4>Thank you, {form.name.split(" ")[0] || "friend"}!</h4>
            <p>Your {isDemo ? "demo request" : "message"} has been received. A member of the Proactive UMS team will be in touch shortly.</p>
            <button className="btn btn--blue" onClick={closeLead} data-testid="lead-success-close">Close</button>
          </div>
        ) : (
          <form className="modal__form" onSubmit={submit}>
            <div className="modal__row">
              <label>
                <span>Full Name<i>*</i></span>
                <input value={form.name} onChange={update("name")} placeholder="Jane Doe" data-testid="lead-name" />
              </label>
              <label>
                <span>Work Email<i>*</i></span>
                <input type="email" value={form.email} onChange={update("email")} placeholder="jane@organization.com" data-testid="lead-email" />
              </label>
            </div>
            <div className="modal__row">
              <label>
                <span>Organization</span>
                <input value={form.organization} onChange={update("organization")} placeholder="Company / Health System" data-testid="lead-org" />
              </label>
              <label>
                <span>Phone</span>
                <input value={form.phone} onChange={update("phone")} placeholder="(555) 000-0000" data-testid="lead-phone" />
              </label>
            </div>
            {isDemo && (
              <label className="modal__full">
                <span>Area of Interest</span>
                <select value={form.interest} onChange={update("interest")} data-testid="lead-interest">
                  <option value="">Select a solution…</option>
                  {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </label>
            )}
            <label className="modal__full">
              <span>Message</span>
              <textarea rows={3} value={form.message} onChange={update("message")} placeholder="How can we help?" data-testid="lead-message" />
            </label>

            {error && <p className="modal__error" data-testid="lead-error">{error}</p>}

            <button type="submit" className="btn btn--green modal__submit" disabled={status === "loading"} data-testid="lead-submit">
              {status === "loading" ? "Sending…" : (isDemo ? "Request a Demo" : "Send Message")}
              {status !== "loading" && <Icon name="ArrowRight" size={16} />}
            </button>
            <p className="modal__note">We respect your privacy. Your information is used only to respond to your request.</p>
          </form>
        )}
      </div>
    </div>
  );
}
